import { create } from 'zustand';
import type { UserProfile } from '../types/domain';

const STORAGE_KEYS = {
  ACCESS_TOKEN: 'accessToken',
  REFRESH_TOKEN: 'refreshToken',
  ID_TOKEN: 'idToken',
  REFRESH_TOKEN_TIMESTAMP: 'refreshTokenTimestamp',
  USER_DETAILS: 'userDetails',
  WARNINGS: 'accountWarnings',
} as const;

export interface AccountWarning {
  id: string;
  message: string;
  severity: 'warning' | 'error';
}

export interface AuthState {
  user: UserProfile | null;
  accessToken: string | null;
  refreshToken: string | null;
  idToken: string | null;
  isAuthenticated: boolean;
  warnings: AccountWarning[];
  login: (
    tokens: { accessToken: string; refreshToken: string; idToken: string },
    user: UserProfile,
    warnings?: AccountWarning[],
  ) => void;
  logout: () => void;
  restoreSession: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  accessToken: null,
  refreshToken: null,
  idToken: null,
  isAuthenticated: false,
  warnings: [],

  login: (tokens, user, warnings) => {
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, tokens.accessToken);
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, tokens.refreshToken);
    localStorage.setItem(STORAGE_KEYS.ID_TOKEN, tokens.idToken);
    localStorage.setItem(
      STORAGE_KEYS.REFRESH_TOKEN_TIMESTAMP,
      Date.now().toString()
    );
    localStorage.setItem(STORAGE_KEYS.USER_DETAILS, JSON.stringify(user));
    if (warnings && warnings.length > 0) {
      localStorage.setItem(STORAGE_KEYS.WARNINGS, JSON.stringify(warnings));
    } else {
      localStorage.removeItem(STORAGE_KEYS.WARNINGS);
    }

    set({
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      idToken: tokens.idToken,
      user,
      isAuthenticated:
        tokens.accessToken !== null && tokens.accessToken !== '',
      warnings: warnings ?? [],
    });
  },

  logout: () => {
    localStorage.removeItem(STORAGE_KEYS.ID_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN_TIMESTAMP);
    localStorage.removeItem(STORAGE_KEYS.USER_DETAILS);
    localStorage.removeItem(STORAGE_KEYS.WARNINGS);

    set({
      user: null,
      accessToken: null,
      refreshToken: null,
      idToken: null,
      isAuthenticated: false,
      warnings: [],
    });
  },

  restoreSession: () => {
    const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
    const idToken = localStorage.getItem(STORAGE_KEYS.ID_TOKEN);
    const userDetailsRaw = localStorage.getItem(STORAGE_KEYS.USER_DETAILS);
    const timestamp = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN_TIMESTAMP);

    // Session expiry check — reject sessions older than 60 minutes
    const SESSION_MAX_AGE_MS = 60 * 60 * 1000;
    if (timestamp) {
      const elapsed = Date.now() - Number(timestamp);
      if (elapsed > SESSION_MAX_AGE_MS) {
        // Expired — clear everything
        localStorage.removeItem(STORAGE_KEYS.ID_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN_TIMESTAMP);
        localStorage.removeItem(STORAGE_KEYS.USER_DETAILS);
        localStorage.removeItem(STORAGE_KEYS.WARNINGS);
        set({ user: null, accessToken: null, refreshToken: null, idToken: null, isAuthenticated: false, warnings: [] });
        return;
      }
    }

    // Validate that we have both a token AND a valid user profile
    let user: UserProfile | null = null;
    if (userDetailsRaw) {
      try {
        const parsed = JSON.parse(userDetailsRaw);
        if (parsed && parsed.userName) {
          user = parsed as UserProfile;
          // Re-validate role against known roles — clear invalid cached roles
          const VALID_ROLES = ['ADMIN', 'SALES_USER', 'BACK_OFFICE_USER', 'FIELD_USER', 'SALES_MANAGER', 'MANAGER'];
          if (user.role && !VALID_ROLES.includes(user.role)) {
            user.role = undefined;
            user.roleAssigned = false;
          }
        }
      } catch {
        user = null;
      }
    }

    // Only authenticate if we have a real token AND a valid user profile
    const isValid = !!accessToken && accessToken.length > 20 && !!user;

    if (!isValid) {
      // Invalid session — clear
      localStorage.removeItem(STORAGE_KEYS.ID_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN_TIMESTAMP);
      localStorage.removeItem(STORAGE_KEYS.USER_DETAILS);
      localStorage.removeItem(STORAGE_KEYS.WARNINGS);
      set({ user: null, accessToken: null, refreshToken: null, idToken: null, isAuthenticated: false, warnings: [] });
      return;
    }

    let warnings: AccountWarning[] = [];
    const warningsRaw = localStorage.getItem(STORAGE_KEYS.WARNINGS);
    if (warningsRaw) {
      try { warnings = JSON.parse(warningsRaw); } catch { /* ignore */ }
    }

    set({
      accessToken,
      refreshToken,
      idToken,
      user,
      isAuthenticated: true,
      warnings,
    });
  },
}));
